import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  fetchNotes, deleteNote,
  fetchQuestionSet, addQuestionToSet, removeQuestionFromSet,
  fetchTopicById,
} from "../../pages/services/notesService";
import { fetchQuestions } from "../../pages/services/questionService";
import "../../styles/CourseNotes.css";

const TABS = ["notes", "quiz", "practice", "assignment"];
const TAB_LABELS = {
  notes: "Notes",
  quiz: "Quiz",
  practice: "Practice Questions",
  assignment: "Assignment Questions",
};

const DiffBadge = ({ level }) => {
  if (!level) return null;
  const cls = `cn-q-badge cn-q-diff cn-diff-${level.toLowerCase().replace(/\s+/g, "-")}`;
  return <span className={cls}>{level}</span>;
};

const TopicContentPage = () => {
  const { courseId, topicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [topicTitle, setTopicTitle] = useState("");
  const [activeTab, setActiveTab] = useState("notes");

  // Notes state
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  // Question set state
  const [questionSet, setQuestionSet] = useState(null);
  const [loadingQSet, setLoadingQSet] = useState(false);

  // Question bank state
  const [allQuestions, setAllQuestions] = useState([]);
  const [qPage, setQPage] = useState(1);
  const [qTotal, setQTotal] = useState(0);
  const [loadingQList, setLoadingQList] = useState(false);
  const [searchQ, setSearchQ] = useState("");

  const [toast, setToast] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  // Fetch topic title
  useEffect(() => {
    fetchTopicById(topicId).then(t => setTopicTitle(t?.title || "")).catch(() => {});
  }, [topicId]);

  // Success toast from navigation state (e.g., after saving a note)
  useEffect(() => {
    if (location.state?.successMessage) {
      showToast(location.state.successMessage);
      window.history.replaceState({}, "");
    }
  }, []);

  // Load content when tab changes
  useEffect(() => {
    if (activeTab === "notes") {
      loadNotes();
    } else {
      loadQuestionSet(activeTab);
      loadQList(1);
    }
  }, [activeTab, topicId]);

  const loadNotes = async () => {
    setLoadingNotes(true);
    try { setNotes(await fetchNotes(topicId)); } catch { setNotes([]); }
    setLoadingNotes(false);
  };

  const loadQuestionSet = async (type) => {
    setLoadingQSet(true);
    try { setQuestionSet(await fetchQuestionSet(topicId, type)); } catch { setQuestionSet(null); }
    setLoadingQSet(false);
  };

  const loadQList = async (page) => {
    setLoadingQList(true);
    try {
      const result = await fetchQuestions(page, 20);
      setAllQuestions(result.data || []);
      setQTotal(result.pagination?.totalItems || 0);
    } catch { setAllQuestions([]); }
    setLoadingQList(false);
  };

  const handlePageChange = (newPage) => {
    setQPage(newPage);
    loadQList(newPage);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteNote(topicId, noteId);
      await loadNotes();
      showToast("Note deleted");
    } catch { showToast("Error deleting note"); }
  };

  const handleAddQuestion = async (questionId) => {
    try {
      const result = await addQuestionToSet(topicId, activeTab, questionId);
      setQuestionSet(result.data);
      showToast("Question added");
    } catch { showToast("Error adding question"); }
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      const result = await removeQuestionFromSet(topicId, activeTab, questionId);
      setQuestionSet(result.data);
      showToast("Question removed");
    } catch { showToast("Error removing question"); }
  };

  const addedQIds = new Set((questionSet?.questions || []).map(q => q._id));

  const filteredQList = allQuestions.filter(q => {
    if (addedQIds.has(q._id)) return false;
    if (!searchQ.trim()) return true;
    const s = searchQ.toLowerCase();
    return q.question?.toLowerCase().includes(s) || q.subject?.toLowerCase().includes(s);
  });

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

  const totalPages = Math.ceil(qTotal / 20);

  return (
    <div className="cn-page">
      {toast && <div className="cn-toast">{toast}</div>}

      <div className="cn-header">
        <div>
          <h2 className="cn-title">{topicTitle || "Topic Content"}</h2>
        </div>
        <button className="cn-back-btn" onClick={() => navigate(`/courses/${courseId}/notes`)}>
          ← Back to Notes
        </button>
      </div>

      {/* Tabs */}
      <div className="cn-tabs">
        {TABS.map(tab => (
          <button
            key={tab}
            className={`cn-tab${activeTab === tab ? " cn-tab-active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* ── Notes Tab ── */}
      {activeTab === "notes" && (
        <div className="cn-tab-content">
          <div className="cn-tab-toolbar">
            <span className="cn-tab-count">
              {notes.length} note{notes.length !== 1 ? "s" : ""}
            </span>
            <button
              className="cn-primary-btn"
              onClick={() => navigate(`/courses/${courseId}/topics/${topicId}/notes/add`)}
            >
              + Add Note
            </button>
          </div>

          {loadingNotes ? (
            <div className="cn-loading">Loading...</div>
          ) : notes.length === 0 ? (
            <p className="cn-empty">No notes yet. Add one above.</p>
          ) : (
            <div className="cn-notes-list">
              {notes.map(note => (
                <div key={note._id} className="cn-note-row">
                  <div className="cn-note-info">
                    <span className="cn-note-title">{note.title}</span>
                    <span className="cn-note-date">{formatDate(note.createdDateTime)}</span>
                  </div>
                  <div className="cn-note-actions">
                    <button
                      className="cn-view-btn"
                      onClick={() =>
                        navigate(`/courses/${courseId}/topics/${topicId}/notes/${note._id}`)
                      }
                    >
                      View
                    </button>
                    <button
                      className="cn-edit-btn"
                      onClick={() =>
                        navigate(`/courses/${courseId}/topics/${topicId}/notes/${note._id}/edit`)
                      }
                    >
                      Edit
                    </button>
                    <button className="cn-del-btn" onClick={() => handleDeleteNote(note._id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Quiz / Practice / Assignment Tabs ── */}
      {activeTab !== "notes" && (
        <div className="cn-tab-content">
          <div className="cn-qset-layout">
            {/* Added Questions Panel */}
            <div className="cn-qset-panel">
              <h3 className="cn-panel-title">
                Added Questions ({(questionSet?.questions || []).length})
              </h3>
              {loadingQSet ? (
                <div className="cn-loading">Loading...</div>
              ) : (questionSet?.questions || []).length === 0 ? (
                <p className="cn-empty">No questions added yet.</p>
              ) : (
                <div className="cn-added-qs">
                  {(questionSet.questions || []).map((q, i) => (
                    <div key={q._id} className="cn-q-row cn-q-added">
                      <div className="cn-q-info">
                        <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                          <span className="cn-q-num">{i + 1}.</span>
                          <span className="cn-q-text">{q.question}</span>
                        </div>
                        <div className="cn-q-meta">
                          {q.subject && <span className="cn-q-badge cn-q-subject">{q.subject}</span>}
                          <DiffBadge level={q.difficultyLevel} />
                        </div>
                      </div>
                      <button className="cn-del-btn" onClick={() => handleRemoveQuestion(q._id)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Question Bank Panel */}
            <div className="cn-qset-panel">
              <h3 className="cn-panel-title">Add from Question Bank</h3>
              <input
                className="cn-q-search"
                placeholder="Search by question text or subject..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
              />
              {loadingQList ? (
                <div className="cn-loading">Loading...</div>
              ) : filteredQList.length === 0 ? (
                <p className="cn-empty">
                  {searchQ ? "No matching questions." : "No questions available."}
                </p>
              ) : (
                <div className="cn-q-picker-list">
                  {filteredQList.map(q => (
                    <div key={q._id} className="cn-q-row">
                      <div className="cn-q-info">
                        <span className="cn-q-text">{q.question}</span>
                        <div className="cn-q-meta">
                          {q.subject && <span className="cn-q-badge cn-q-subject">{q.subject}</span>}
                          <DiffBadge level={q.difficultyLevel} />
                        </div>
                      </div>
                      <button className="cn-add-q-btn" onClick={() => handleAddQuestion(q._id)}>
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {qTotal > 20 && (
                <div className="cn-q-pagination">
                  <button disabled={qPage <= 1} onClick={() => handlePageChange(qPage - 1)}>
                    ← Prev
                  </button>
                  <span>Page {qPage} of {totalPages}</span>
                  <button disabled={qPage >= totalPages} onClick={() => handlePageChange(qPage + 1)}>
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicContentPage;
